import { z } from "zod";
import { type Tag, Tags } from "./tags";

const GooglePlacePriceLevel = z.enum([
    "PRICE_LEVEL_FREE",
    "PRICE_LEVEL_INEXPENSIVE",
    "PRICE_LEVEL_MODERATE",
    "PRICE_LEVEL_EXPENSIVE",
    "PRICE_LEVEL_VERY_EXPENSIVE",
]);
type GooglePlacePriceLevel = z.infer<typeof GooglePlacePriceLevel>;

const GooglePlacePriceLevelMap: Record<GooglePlacePriceLevel, Tag> = {
    PRICE_LEVEL_FREE: Tags.price.free,
    PRICE_LEVEL_INEXPENSIVE: Tags.price.inexpensive,
    PRICE_LEVEL_MODERATE: Tags.price.moderate,
    PRICE_LEVEL_EXPENSIVE: Tags.price.expensive,
    PRICE_LEVEL_VERY_EXPENSIVE: Tags.price.veryExpensive,
};

// Price level https://developers.google.com/maps/documentation/places/web-service/reference/rest/v1/places
export function mapGooglePlacePriceLevel(googlePlace: GooglePlace): Tag[] {
    const parsedLevel = GooglePlacePriceLevel.safeParse(googlePlace.priceLevel);
    if (!parsedLevel.success) return [];
    return [GooglePlacePriceLevelMap[parsedLevel.data]];
}

export function mapGooglePlaceDetailsToTags(place: GooglePlace): Tag[] {
    const tags: Tag[][] = [
        place.servesBreakfast ? ["meals:breakfast"] : [],
        place.servesBrunch ? ["meals:brunch"] : [],
        place.servesLunch ? ["meals:lunch"] : [],
        place.servesDinner ? ["meals:dinner", "type:restaurant"] : [],
        place.liveMusic ? ["type:live-music"] : [],
    ];

    return tags.flat();
}

export const GooglePlaceBusinessStatus = z.enum([
    "OPERATIONAL",
    "CLOSED_TEMPORARILY",
    "CLOSED_PERMANENTLY",
    "UNAVAILABLE",
]);
export type GooglePlaceBusinessStatus = z.infer<typeof GooglePlaceBusinessStatus>;

export const GooglePlaceBusinessStatusMap: Record<GooglePlaceBusinessStatus, string> = {
    CLOSED_PERMANENTLY: "Closed permanently",
    CLOSED_TEMPORARILY: "Closed temporarily",
    OPERATIONAL: "Operational",
    UNAVAILABLE: "Business status unavailable",
};

export const GooglePlacePhoto = z.object({
    name: z.string(),
    heightPx: z.number().nullish(),
    widthPx: z.number().nullish(),
    // Note - photoUri is not part of Place APIs response
    // Update it post fetching images using Google Place Image APIs
    photoUri: z.string().nullish(),
});

export type GooglePlacePhoto = z.infer<typeof GooglePlacePhoto>;

export const GoogleGeoPoint = z.object({
    latitude: z.number(),
    longitude: z.number(),
});

export type GoogleLocation = z.infer<typeof GoogleGeoPoint>;

export const GooglePlaceText = z.object({
    text: z.string().nullish(),
    languageCode: z.string().nullish(),
});

export type GooglePlaceText = z.infer<typeof GooglePlaceText>;

export const GoogleReview = z.object({
    name: z.string().nullish(),
    rating: z.number().nullish(),
    text: GooglePlaceText.nullish(),
    originalText: GooglePlaceText.nullish(),
});

export type GoogleReview = z.infer<typeof GoogleReview>;

export const GooglePlaceOpenClosePeriod = z.object({
    day: z.number(),
    hour: z.number(),
    minute: z.number(),
});

export type GooglePlaceOpenClosePeriod = z.infer<typeof GooglePlaceOpenClosePeriod>;

export const GooglePlaceAddressComponent = z.object({
    longText: z.string().nullish(),
    shortText: z.string().nullish(),
    types: z.array(z.string()).nullish(),
    languageCode: z.string().nullish(),
});

export type GooglePlaceAddressComponent = z.infer<typeof GooglePlaceAddressComponent>;

export const GooglePlace = z.object({
    id: z.string(),
    displayName: z
        .object({
            text: z.string().nullish(),
            languageCode: z.string().nullish(),
        })
        .nullish(),

    websiteUri: z.string().nullish(),
    googleMapsUri: z.string().nullish(),

    internationalPhoneNumber: z.string().nullish(),

    businessStatus: GooglePlaceBusinessStatus.nullish(),

    priceLevel: z.string().nullish(),

    types: z.array(z.string()).nullish(),
    primaryType: z.string().nullish(),
    primaryTypeDisplayName: GooglePlaceText.nullish(),

    photos: z.array(GooglePlacePhoto).nullish(),

    rating: z.number().nullish(),
    userRatingCount: z.number().nullish(),
    userRatingsTotal: z.number().nullish(),
    reviews: z.array(GoogleReview).nullish(),

    // geometry
    location: GoogleGeoPoint.nullish(),
    viewport: z
        .object({
            low: GoogleGeoPoint.nullish(),
            high: GoogleGeoPoint.nullish(),
        })
        .nullish(),

    addressComponents: z.array(GooglePlaceAddressComponent).nullish(),
    formattedAddress: z.string().nullish(),

    utcOffsetMinutes: z.number().nullish(),

    regularOpeningHours: z
        .object({
            periods: z.array(
                z.object({
                    open: GooglePlaceOpenClosePeriod.nullish(),
                    close: GooglePlaceOpenClosePeriod.nullish(),
                }),
            ),
            weekdayDescriptions: z.array(z.string()).nullish(),
        })
        .nullish(),

    servesBreakfast: z.boolean().nullish(),
    servesBrunch: z.boolean().nullish(),
    servesLunch: z.boolean().nullish(),
    servesDinner: z.boolean().nullish(),
    liveMusic: z.boolean().nullish(),
    reservable: z.boolean().nullish(),
});

export type GooglePlace = z.infer<typeof GooglePlace>;

export type GooglePlaceFields = Partial<keyof GooglePlace>[];
